/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

import { FormElement } from "../FormElement";
import { IFormElementConfig } from "../IFormElementConfig";
import { IFormElementFactory } from "../IFormElementFactory";
import { FormModel } from "../FormModel";
import { FORM_EL_TYPE } from "../FormElementTypes";
import { FormSection } from "./FormSection";
import { IFormExpressionFn } from "../IFormExpressionFn";

/**
 * Element config
 */
export interface IFormSectionGroupConfig extends IFormElementConfig {
	/** Section contents */
	content?: Array<IFormElementFactory<FormSection>>;

	/** If to display space before section */
	spaceBefore?: boolean;

	/** If to display space after section */
	spaceAfter?: boolean;

	/** If to display a row */
	display?: IFormExpressionFn<FormSectionGroup, boolean>;

	/** If the row should be printable */
	print?: boolean;
}

/**
 * Section group
 */
export class FormSectionGroup extends FormElement<IFormSectionGroupConfig> {

	/** Content elements */
	protected content: Array<FormElement<any>> = [];

	/** Display value */
	protected display: boolean;

	/**
	 * Element constructor
	 *
	 * @param model Model instance
	 * @param parent Parent element
	 * @param config Element configuration
	 */
	public constructor(
		protected model: FormModel,
		protected parent: FormElement<any>,
		protected config: IFormSectionGroupConfig
	) {

		super(model, parent, FORM_EL_TYPE.SECTION_GROUP, config);

		// Init content components
		if (config.content) {

			for (let i = 0; i < config.content.length; i++) {
				this.content.push(
					config.content[i](this.model, this)
				);
			}

		}

	}

	/**
	 * Updates the element
	 *
	 * @param resetState If to reset state
	 */
	public update(resetState: boolean = false) {

		// Call update on all content elements
		for (let i = 0; i < this.content.length; i++) {
			this.content[i].update(resetState);
		}

		// Add late computation if display is defined
		if (typeof this.config.display === "function") {
			this.model._addExpression(() => {

				this.display = this.config.display(this.model, this);
				this.emit("update");

			}, 1000);
		} else {
			this.display = true;
		}

	}

	/**
	 * Returns element state
	 */
	public getState() {

		return {
			...super.getState(),
			content: this.content,
			display: this.display
		};

	}

	/**
	 * Destroys the element
	 */
	public destroy() {

		// Call destroy on all content elements
		for (let i = 0; i < this.content.length; i++) {
			this.content[i].destroy();
		}

		super.destroy();

	}

}

/**
 * Declares section group
 *
 * @param config Config
 * @param content Content elements
 */
export function SectionGroup(
	config: IFormSectionGroupConfig,
	content: Array<IFormElementFactory<FormSection>>
) {

	const _config = {
		content: content,
		...config
	};

	return (model, parent) => new FormSectionGroup(model, parent, _config);

}
